import { useEvent } from 'expo';
import { useVideoPlayer, VideoView } from 'expo-video';
import type { ViewStyle } from 'react-native';
import { View, XStack, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSIcon from '@/components/ui/PSIcon';
import { usePlayWhileFocused } from '@/src/hooks/usePlayWhileFocused';

/**
 * PSUpsellBanner — the Access Pass upsell hero (molecule).
 *
 * A looping showreel with the pitch and one CTA sitting on top of it: the
 * "at just ₹999 / start trial now" block from the non-member home page.
 *
 * Render-only, and aggressively so: it takes the finished `titleString` (the
 * price is already baked into it) rather than a price + a template. Only the
 * screen knows whether the copy is a trial price, a renewal nudge or a
 * win-back, and no string transform here would get that right — the banner's
 * job is the video + the layout, not the sentence. Same for `ctaTitle`.
 *
 * ── The reel is hardwired ─────────────────────────────────────────────
 * `SHOWREEL_URL` is a constant, not a prop. There is exactly one Access Pass
 * showreel, and a `source` prop would invite a second video through a component
 * whose entire look (the 3:4 crop, white text, no scrim) is tuned for this one.
 * When a second reel exists, it earns the prop then.
 *
 * ── Only control is tap-to-mute ───────────────────────────────────────
 * `nativeControls={false}` — no scrubber, no play/pause, no fullscreen. It
 * autoplays muted (the only thing browsers allow without a gesture) and loops,
 * so it reads as motion design rather than as a video the user has to operate.
 * The single affordance is a tap anywhere to toggle sound.
 *
 * The tap target is the OVERLAY, not the video. Whether a native `VideoView`
 * lets touches through to a parent is platform-dependent (iOS player layer vs
 * Android SurfaceView vs a web `<video>` element), so the overlay — which
 * already covers the whole frame — owns `onPress` instead. The nested
 * `PSButton` keeps its own tap: RN gives the press to the innermost responder
 * and does NOT bubble it, so hitting the CTA never toggles sound.
 *
 * `muted` comes off the player's own `mutedChange` event rather than local
 * state, so the speaker glyph can't drift from what the player is actually
 * doing (the OS mutes for us — a route change, a call, silent mode).
 *
 * The speaker icon exists because tap-to-unmute is otherwise INVISIBLE: nothing
 * else on screen says there's audio, or that the frame is tappable at all.
 * It's an indicator, not a button — the whole banner is the target, so it isn't
 * a `PSCircleButton` and carries no backing chip.
 *
 * ── No scrim ──────────────────────────────────────────────────────────
 * Deliberate: the reel is graded dark enough behind the text, so the video is
 * shown as shot rather than under a gradient. That's a bet on THIS footage —
 * if the reel is ever recut brighter, the fix is a scrim here, not a
 * `textColor` prop (dark text over video is not a look this component has).
 *
 * ── Pauses when the screen isn't focused ──────────────────────────────
 * See [[usePlayWhileFocused]] — home stays mounted under whatever is pushed on
 * top of it, so the reel has to be stopped by hand.
 */

// Exported so AccessPassShowreel plays the SAME reel from one source, rather
// than a second copy of the URL that can drift.
export const SHOWREEL_URL =
  'https://pub-e7747d96903d47c79720752ecf955a6b.r2.dev/ap-showreel-5.mp4';

// Portrait 3:4 (width:height), matching EventCard / ImageCarousel. As an
// `aspectRatio` rather than a measured width → height, since nothing here needs
// the pixel width (ImageCarousel only measures because paging needs `scrollTo`).
const RATIO = 3 / 4;

/**
 * The fill style every reel's VideoView takes. Exported for the same reason as
 * SHOWREEL_URL: [[AccessPassShowreel]] plays the same video the same way, and a
 * second copy is a second thing to get wrong.
 *
 * Spelled out rather than `StyleSheet.absoluteFill`: insets alone leave the reel
 * at its intrinsic size on web, where a <video> is a replaced element and
 * `inset: 0` doesn't size one. The percentages are what fill the box.
 */
/* Plain RN style object, not Tamagui props: `$token` values wouldn't resolve
   here, but the rule can't tell the two apart. */
/* eslint-disable local-rules/no-hardcoded-tokens */
export const SHOWREEL_VIDEO_STYLE: ViewStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
};
/* eslint-enable local-rules/no-hardcoded-tokens */

export default function PSUpsellBanner({
  ctaTitle,
  onPressCta,
}: {
  /** The CTA label — e.g. "start trial now". */
  ctaTitle: string;
  onPressCta?: () => void;
}) {
  const player = useVideoPlayer(SHOWREEL_URL, (p) => {
    p.loop = true;
    // Muted BEFORE play(): an unmuted autoplay is refused outright on web, and
    // sound the user didn't ask for is the wrong first impression anyway.
    p.muted = true;
    p.play();
  });

  const { muted } = useEvent(player, 'mutedChange', { muted: player.muted });

  usePlayWhileFocused(player);

  return (
    <View alignSelf="stretch" aspectRatio={RATIO} overflow="hidden">
      {/* Styled with a plain RN object: VideoView isn't a Tamagui component, so
          it can't take our `$token` props — and taking it out of flow is what
          lets the overlay below lay out normally (no absolute positioning, no
          raw inset numbers) and simply paint on top. */}
      <VideoView
        style={SHOWREEL_VIDEO_STYLE}
        player={player}
        contentFit="cover"
        nativeControls={false}
        // Web-only: keep the reel in its box instead of the browser hijacking it
        // into a fullscreen player.
        playsInline
      />

      {/* The overlay fills the frame (flex={1} inside the ratio box) and is the
          tap target for mute — see the docstring. */}
      <YStack
        flex={1}
        p="$lg"
        gap="$lg"
        // Web: the frame is interactive, so it says so (same as ImageCarousel's
        // arrows/dots). On device this prop is a no-op.
        cursor="pointer"
        // `player` is a native SharedObject, not React state: assigning `muted`
        // IS expo-video's documented setter (there is no setMuted), and the
        // write goes to the native player, which then emits `mutedChange` for
        // the glyph above. The compiler's "don't mutate a hook's return value"
        // rule can't tell that apart from mutating a memoized object.
        // eslint-disable-next-line react-hooks/immutability
        onPress={() => (player.muted = !muted)}
        justifyContent="flex-end"
      >
        <XStack justify="flex-end">
          <PSIcon
            name={muted ? 'volume-mute' : 'volume-high'}
            color="$textWhite"
          />
        </XStack>
        <PSButton
          title={ctaTitle}
          onPress={onPressCta}
          fullWidth
          icon={<PSIcon name="arrow-forward" color="$textWhite" size={18} />}
        />
      </YStack>
    </View>
  );
}
